import { Component, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Representation } from '../../../api/app-types/representation.app';
import { provideUserApi, USER_SERVICE } from '../../../api/services/user.service';
import { provideValidationApi, VALIDATION_SERVICE } from '../../../api/services/validation.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { CurrentUserService } from '../../services/current-user.service';
import { RECORD_MANAGER } from '../../services/record.provide';
import { REPRESENTATION_MODE } from '../../services/representation-mode.service';
import { SidebarButton } from './sidebar-button';
import { SidebarHelpMenu } from './sidebar-help-menu/sidebar-help-menu';
import { SidebarSettingsMenu } from './sidebar-settings-menu';

/**
 * Component for the sidebar, typically used for navigation or actions.
 * It includes buttons that can either navigate to a route or trigger a click event.
 * Example usage:
 * ```html
 * <lvr-sidebar>
 *   <lvr-sidebar-button icon="home" label="Home" [route]="['/home']"></lvr-sidebar-button>
 *   <lvr-sidebar-button icon="settings" label="Settings" (click)="openSettings()"></lvr-sidebar-button>
 * </lvr-sidebar>
 * ```
 */
@Component({
	selector: 'lvr-sidebar',
	imports: [
		RouterModule,
		MatDividerModule,
		MatButtonModule,
		MatIconModule,
		SidebarButton,
		SidebarSettingsMenu,
		TranslocoPipe,
		SidebarHelpMenu,
	],
	templateUrl: './sidebar.html',
	styleUrl: './sidebar.scss',
	providers: [provideUserApi, provideValidationApi],
})
export class Sidebar {
	private authService = inject(MsalService);
	private draftService = inject(FormPersistenceService);
	private recordManager = inject(RECORD_MANAGER);
	private representationMode = inject(REPRESENTATION_MODE);
	private userService = inject(USER_SERVICE);
	private currentUserService = inject(CurrentUserService);
	private router = inject(Router);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly env = inject(ENVIRONMENT);
	private readonly sessionManager = inject(SessionManagerService);
	public readonly currentUserId = signal<string>('');
	public readonly representations = signal<Representation[]>([]);
	public readonly representationsLoading = signal(false);
	public readonly canReceiveRepresentation = signal(true);

	public constructor() {
		// Can receive representation
		effect(() => {
			void this.validateCanBeRepresented();
		});
	}

	public async logout() {
		const account = this.authService.instance.getAllAccounts()[0];
		this.sessionManager.endSession(account?.localAccountId);

		if (account) {
			this.representationMode.clear();
			this.draftService.deleteAllDrafts();
			this.recordManager.closeAllRecords();
			this.currentUserService.clear();
			await this.authService.instance.clearCache();
			this.authService.logoutRedirect({
				account: account,
			});
		}
	}

	public async loadRepresentations() {
		if (this.representationsLoading()) return;
		this.representationsLoading.set(true);
		try {
			const userId = await this.getCurrentUserId();
			if (!userId) {
				this.representations.set([]);
				return;
			}
			const response = await this.userService.getRepresentations(
				userId,
				this.env.pagination.offset,
				this.env.pagination.range,
			);
			this.representations.set(response.entities);
		} catch (error) {
			console.error('Failed to load representations:', error);
			this.representations.set([]);
		} finally {
			this.representationsLoading.set(false);
		}
	}

	public selectRepresentative(user: Representation) {
		// Switch to representation record manager context and close current tabs
		this.recordManager.switchToRepresentationContext(Number(user.id));
		this.recordManager.closeAllRecords();

		this.representationMode.activate({ id: user.id, name: this.getUserLabel(user) });
		void this.router.navigate(['/']);
	}

	public getUserLabel(user: Representation) {
		const name = `${user.firstName} ${user.lastName}`.trim();
		return name || user.displayName;
	}

	private async getCurrentUserId() {
		const existing = this.currentUserId();
		if (existing) return existing;
		const me = await this.currentUserService.getCurrentUser();
		this.currentUserId.set(me.id);
		return me.id;
	}

	private async validateCanBeRepresented() {
		this.canReceiveRepresentation.set(true);
		try {
			const userId = await this.getCurrentUserId();
			const validation = await this.validationApi.validateCanReceiveRepresentation(userId);

			this.canReceiveRepresentation.set(validation);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(
					this.transloco.translate('base.notification_message.user_representation_validation_failed'),
				),
			);
			this.canReceiveRepresentation.set(true);
		}
	}
}
